import { useEffect, useState } from "react";

import {
  Button,
  Modal,
  Flex,
  Form,
  Input,
  Select,
  message,
} from "antd";

import {
  GrossImage,
  getGrossImageList,
  postGrossImage,
  updateGrossImage,
} from "../../../api/postAPI";
import { PostCase } from "../../../api/postCaseAPI";
import CaseFileGroups, {
  CaseFileGroup,
  UNASSIGNED_VALUE,
  UPLOAD_MODAL_BODY_STYLE,
  createCaseFileGroup,
  findDuplicateAcrossGroups,
  flattenCaseFileGroups,
} from "./CaseFileGroups";
import UploadProgressModal, { useUploadProgress } from "../../upload/UploadProgressModal";

interface Props {
  postId: number;
  setImageList: React.Dispatch<React.SetStateAction<GrossImage[]>>;
  showAddImageModal: boolean;
  setShowAddImageModal: React.Dispatch<React.SetStateAction<boolean>>;
  currentImage: GrossImage | null;
  setCurrentImage: React.Dispatch<React.SetStateAction<GrossImage | null>>;
  showEditImageModal: boolean;
  setShowEditImageModal: React.Dispatch<React.SetStateAction<boolean>>;
  cases?: PostCase[];
  defaultCaseId?: number | null;
}

const AddGrossImage = ({
  postId,
  setImageList,
  showAddImageModal,
  setShowAddImageModal,
  currentImage,
  setCurrentImage,
  showEditImageModal,
  setShowEditImageModal,
  cases = [],
  defaultCaseId = null,
}: Props) => {
  const [uploadForm] = Form.useForm();
  const [editForm] = Form.useForm<GrossImage>();
  const [caseGroups, setCaseGroups] = useState<CaseFileGroup[]>(() => [createCaseFileGroup()]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { progress, start, startFile, updateFile, finish } = useUploadProgress();

  useEffect(() => {
    if (showAddImageModal) {
      setCaseGroups([createCaseFileGroup(defaultCaseId ?? UNASSIGNED_VALUE)]);
    }
  }, [showAddImageModal, defaultCaseId]);

  const uploadImage = async () => {
    if (isSubmitting) return;
    const entries = flattenCaseFileGroups(caseGroups);
    if (entries.length === 0) {
      message.error("Please upload a file");
      return;
    }
    const duplicated = findDuplicateAcrossGroups(caseGroups);
    if (duplicated) {
      message.error(`${duplicated}: 여러 Case 에 같은 파일이 들어 있습니다.`);
      return;
    }

    setIsSubmitting(true);
    start(entries.length);
    let failCount = 0;
    let lastErrorMsg = "";

    for (let i = 0; i < entries.length; i++) {
      const { item: fileItem, postCaseId } = entries[i];
      startFile(i, fileItem.name);
      const payload: GrossImage = {
        postId,
        ...(postCaseId ? { postCaseId } : {}),
        file: {
          file: fileItem.originFileObj,
          fileList: [fileItem],
        },
      };

      const result = await postGrossImage(payload, (ratio) => updateFile(i, ratio));
      if (!result.success) {
        failCount++;
        lastErrorMsg = result.message || "";
      }
    }

    setImageList(await getGrossImageList(postId));
    finish();
    setIsSubmitting(false);

    if (failCount > 0) {
      message.error(
        `${entries.length}개 중 ${failCount}개 파일 업로드에 실패했습니다.${lastErrorMsg ? ` (${lastErrorMsg})` : ""}`
      );
    } else {
      message.success(`${entries.length}개의 육안 이미지가 등록되었습니다.`);
    }

    setCaseGroups([createCaseFileGroup()]);
    setShowAddImageModal(false);
  };

  const editImage = async (values: GrossImage) => {
    if (!currentImage?.grossPhotoId) {
      message.error("수정할 이미지를 찾을 수 없습니다.");
      return;
    }

    values.grossPhotoId = currentImage.grossPhotoId;
    const result = await updateGrossImage(values);

    if (!result.success) {
      message.error(result.message);
      return;
    }

    const newImageList = await getGrossImageList(postId);
    setImageList(newImageList);
    setCurrentImage(
      newImageList.find(
        (photo) => photo.grossPhotoId === currentImage.grossPhotoId
      ) || null
    );
    message.success("수정되었습니다.");
    setShowEditImageModal(false);
  };

  const cancelUpload = () => {
    setCaseGroups([createCaseFileGroup()]);
    setShowAddImageModal(false);
  };

  const cancelEdit = () => {
    editForm.resetFields();
    setShowEditImageModal(false);
  };

  useEffect(() => {
    if (currentImage) {
      editForm.setFieldsValue(currentImage);
    }
  }, [currentImage]);

  return (
    <>
      <Modal
        centered
        title="Upload Gross Image"
        styles={{ body: UPLOAD_MODAL_BODY_STYLE }}
        open={showAddImageModal}
        onCancel={cancelUpload}
        footer={null}
        destroyOnClose
      >
        <Flex gap="middle" vertical>
          <Form form={uploadForm} onFinish={uploadImage} layout="vertical">
            <CaseFileGroups
              cases={cases}
              groups={caseGroups}
              setGroups={setCaseGroups}
              mode="image"
              enableScroll={false}
            />
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={cancelUpload} style={{ marginRight: "10px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={isSubmitting}>
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Flex>
      </Modal>
      <UploadProgressModal {...progress} />

      <Modal
        centered
        title="Edit gross image data"
        open={showEditImageModal}
        onCancel={cancelEdit}
        footer={null}
        destroyOnClose
      >
        <Flex gap="middle" vertical>
          <Form
            form={editForm}
            onFinish={editImage}
            initialValues={currentImage || undefined}
            layout="vertical"
          >
            <Form.Item name="caption" label="Image Name">
              <Input />
            </Form.Item>
            <Form.Item name="zindanName" label="Diagnosis">
              <Input />
            </Form.Item>
            <div style={{ display: "flex", gap: "2rem", margin: "9px 0" }}>
              <Form.Item name="gender" label="Gender" style={{ flex: "50%" }}>
                <Select>
                  <Select.Option value="M">Male</Select.Option>
                  <Select.Option value="F">Female</Select.Option>
                  <Select.Option value="U">Unknown</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="age" label="Age" style={{ flex: "50%" }}>
                <Input />
              </Form.Item>
            </div>
            <Form.Item name="site" label="Site">
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={cancelEdit} style={{ marginRight: "10px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Flex>
      </Modal>
    </>
  );
};

export default AddGrossImage;